import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  styled,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import Page from '../components/Page';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { store } from '../store';
import { } from '../features/cuentacorriente/ctaCorrienteSlice';
import {
  ParsedCuota,
  getCuotasByResource,
  getCuotasList,
  getCuotasStatus,
} from '../features/cuentacorriente/ctaCorrienteSlice';
import CtaCorriente from '../components/CtaCorriente';
import { cleanOfflineImponibleState, offlineImponible } from '../features/offlineImponible/offlineImponibleSlice';
import config from '../config';
import { Recurso } from '../features/imponibles/imponiblesAPI';

import { SearchedImponible } from '../features/offlineImponible/offlineImponibleAPI';
import { createComprobante, getComprobante } from '../features/comprobantes/comprobantesSlice';
import { getImageComprobante } from '../utils';

// import { getFotomultas } from '../features/fotomultas/fotoMultasAPI';
// import FotoMulta from '../components/FotoMulta';

const InfoWrapper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  padding: '1.5rem 1rem',
}));

const ButtonWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  padding: '1rem',
}));

const ExpirationWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  padding: '1rem 2rem 1rem 1.5rem',
}));

const Resource = styled(Button)(() => ({
  lineHeight: '1.2',
  fontWeight: '700',
  marginRight: '1rem',
  padding: '.5rem 1rem',
  textTransform: 'uppercase',
  textAlign: 'left',
  width: '10rem',
  fontSize: '.8rem',
}));

const CustomButton = styled(Button)(() => ({
  marginLeft: 10,
  padding: '.4rem 2rem',
  fontSize: '.75rem',
  fontWeight: '700',
}));

const getTipoImponible = (imponible: SearchedImponible) => {
  if (imponible.dominio) {
    return 'ROD';
  } else if (imponible.nro_sepult) {
    return 'CEM';
  } else if (imponible.par_catastral) {
    return 'INM';
  } else if (imponible.nro_comercio) {
    return 'COM';
  } else {
    return 'CON';
  }
};

const getLabelImponible = (imponible: SearchedImponible) => {
  switch (getTipoImponible(imponible)) {
    case 'ROD': return `Dominio: ${imponible.dominio}`;
    case 'CEM': return `N° Nicho: ${imponible.nro_sepult}`;
    case 'INM': return `N° Partida: ${imponible.par_catastral}`;
    case 'COM': return `N° Comercio: ${imponible.nro_comercio}`;
    case 'CON': return `CUIT: ${imponible.cuim}`;
    default: return `CUIT: ${imponible.cuit}`;
  }
};

const getIdImponible = (imponible: SearchedImponible): string => {
  switch (getTipoImponible(imponible)) {
    case 'ROD': return imponible.nro_rodado!;
    case 'CEM': return imponible.nro_sepult!;
    case 'INM': return imponible.par_catastral!;
    case 'COM': return imponible.nro_comercio!;
    case 'CON': return imponible.nro_contrib!;
    default: return imponible.cuit!;
  }
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

export default function OfflinePayment() {
  const theme = useTheme();
  const navigate = useNavigate();

  const imponible = useSelector(offlineImponible);

  // Controlador de boton Tabs
  const [value, setValue] = useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [selectedResource, setSelectedResource] = useState<null | Recurso>(null);

  const cuotas = useSelector(getCuotasList);
  const cuotasStatus = useSelector(getCuotasStatus);

  const [selectedCuotas, setSelectedCuotas] = useState<ParsedCuota[]>([]);

  const [total, setTotal] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const [isLoading, setLoading] = useState(false);

  // const [isFotomultas, setIsFotomultas] = useState(false);
  // const [data, setData] = useState([]);
  // const [error, setError] = useState<string | null>(null);

  // const fetchFotomultas = async (dominio: string) => {
  //   try {
  //     const response = await store.dispatch(getFotomultas(dominio));

  //     console.log(response);

  //     if (Array.isArray(response.payload)) {
  //       setData(response.payload);
  //     } else {
  //       setError("No se encontraron datos");
  //     }

  //   } catch (err) {
  //     console.error("Error al cargar las fotomultas", err);
  //     setError("Error al cargar las fotomultas");
  //   }
  // };

  const periodCuotas = selectedCuotas
    .map(cuota => ('0' + cuota.cuota).slice(-2) + '/' + cuota.anio)
    .join(',');

  useEffect(() => {
    if (imponible !== undefined) {
      const data = {
        tipo: getTipoImponible(imponible),
        id: getTipoImponible(imponible) === 'CON'
          ? imponible.nro_contrib!
          : getIdImponible(imponible),
        recurso: imponible?.recursos[0].recurso,
      };

      if (getTipoImponible(imponible) == 'CON' && data.recurso == '14') {
        setTextVisible(false)
      };

      setSelectedResource(imponible?.recursos[0]);
      store.dispatch(getCuotasByResource(data));
      store.dispatch(cleanOfflineImponibleState());
    }
  }, [imponible]);

  const handlePayment = () => {
    if (!imponible || !selectedResource) {
      return;
    }

    store.dispatch(createComprobante({
      tipo: getTipoImponible(imponible),
      imponible: getIdImponible(imponible),
      recurso: selectedResource.recurso,
      periods: selectedCuotas.map(c => c.cuota.padStart(2, '0') + '/' + c.anio),
    })).unwrap().then(() => {
      navigate('/paga-tu-tasa/consulta/pago');
    });
  };

  const handleAnualPayment = () => {
    if (!imponible || !selectedResource || !selectedResource.anual) {
      return;
    }

    setLoading(true);

    store.dispatch(getComprobante({
      tipo: 18,
      numero: parseInt(selectedResource.anual),
    })).unwrap()
      .then(() => {
        navigate('/paga-tu-tasa/consulta/pago');
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    // Cada vez que se cambie de recurso o entre cuenta corriente y plan de pago, reseteamos los datos
    setTotal(0);
    setSelectedCuotas([]);
  }, [value, selectedResource]);

  // useEffect(() => {
  //   fetchFotomultas(imponible.dominio);
  // }, []);

  return (
    <Page>
      <Container>
        <Grid container padding={'1.875rem'}>
          <Grid item xs={12} mb={2}>
            <InfoWrapper>
              {
                !imponible
                  ? <CircularProgress size={20} />
                  : <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <img
                      src={getImageComprobante(imponible.tipo)}
                      style={{ alignSelf: 'center', height: '40px' }}
                    />
                    <Box ml={2}>
                      <Typography variant="body1">
                        Destinatario: {imponible?.resp_pago}
                      </Typography>
                      <Typography variant="body1">
                        {getLabelImponible(imponible)}
                      </Typography>
                      {imponible.debito ? <Typography variant="body1" sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: '#36B7E8',
                      }}>Este imponible está adherido al Débito Automático</Typography> : <Typography variant="body1" sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: '#e83636',
                      }}>Sin adhesión al Débito Automático.</Typography>}
                    </Box>
                  </Box>
              }
            </InfoWrapper>
          </Grid>
          <Grid item xs={12}>
            <ButtonWrapper>
              <Paper
                sx={{
                  backgroundColor: 'rgba(245,248,250,1)',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  padding: '1rem',
                }}
              >
                {
                  imponible?.recursos.map((resource, i) => (
                    <Resource
                      onClick={() => {
                        // setIsFotomultas(false);
                        setSelectedResource(resource);
                        const data = {
                          tipo: getTipoImponible(imponible),
                          id: getIdImponible(imponible),
                          recurso: resource.recurso,
                        };
                        store.dispatch(getCuotasByResource(data));
                      }}
                      key={i}
                      sx={{
                        backgroundColor: selectedResource?.recurso === resource.recurso
                          ? theme.palette.primary.main
                          : 'transparent',
                        color: selectedResource?.recurso === resource.recurso
                          ? 'white'
                          : theme.palette.grey[700],
                      }}
                    >
                      {resource.descripcion}
                    </Resource>
                  ))
                }

                {/* {(selectedResource?.recurso === "30" || selectedResource?.recurso === "60") && (
                  <Resource
                    onClick={() => setIsFotomultas(true)}
                    sx={{
                      backgroundColor: isFotomultas ? theme.palette.primary.main : 'rgb(54, 183, 232)',
                      color: 'white',
                    }}
                  >
                    MULTAS
                  </Resource>
                )} */}
              </Paper>
            </ButtonWrapper>
          </Grid>

          <Grid item xs={12} mt={2}>
            <ExpirationWrapper>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={value} onChange={handleChange}>
                    <Tab label="Cuenta Corriente" {...a11yProps(0)} />
                  </Tabs>
                </Box>
              </Box>

              <CtaCorriente
                value={value}
                cuotas={cuotas}
                selectedCuotas={selectedCuotas}
                setSelectedCuotas={setSelectedCuotas}
                total={total}
                setTotal={setTotal}
                status={cuotasStatus}
                textVisible={selectedResource?.recurso == "14" ? false : true}
              />

              {/* {isFotomultas ? (
                <FotoMulta infracciones={data} />
              ) : (
                <CtaCorriente
                  value={value}
                  cuotas={cuotas}
                  selectedCuotas={selectedCuotas}
                  setSelectedCuotas={setSelectedCuotas}
                  total={total}
                  setTotal={setTotal}
                  status={cuotasStatus}
                  textVisible={selectedResource?.recurso == "14" ? false : true}
                />
              )} */}

              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                <CustomButton
                  variant="outlined"
                  disabled={isLoading || periodCuotas === '' ? true : false}
                  onClick={() => {
                    if (!imponible) {
                      return;
                    }
                    const imprimirUrl = `${config.api.url}/pdf/boleta/${getTipoImponible(imponible)}-${getIdImponible(imponible)
                      }/${selectedResource?.recurso}?periodos=${periodCuotas}`;

                    window.open(imprimirUrl);
                  }}
                >
                  Imprimir
                </CustomButton>

                <CustomButton
                  variant="outlined"
                  disabled={isLoading || selectedResource?.anual === null ? true : false}
                  onClick={() => {
                    const comprobanteURL = `${config.api.url}/pdf/comprobante/18-${selectedResource?.anual}`;
                    window.open(comprobanteURL);
                  }}
                >
                  Comprobante {selectedResource?.anual && selectedResource?.semestral ? 'Semestral' : 'Anual'}
                </CustomButton>

                <CustomButton
                  variant="outlined"
                  disabled={isLoading || selectedResource?.anual === null ? true : false}
                  onClick={() => {
                    const comprobanteURL = `${config.target.url}/paga-tu-tasa/qr/18-${selectedResource?.anual}`;
                    window.open(comprobanteURL);
                  }}
                >
                  Pago {selectedResource?.anual && selectedResource?.semestral ? 'Semestral' : 'Anual'}
                </CustomButton>

                <CustomButton
                  variant="contained"
                  disabled={isLoading || !selectedCuotas || !selectedCuotas.length || !cuotas.length}
                  onClick={handlePayment}
                  sx={{ color: 'white' }}
                >
                  PAGAR AHORA
                </CustomButton>
              </Box>

              {/* <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                {!isFotomultas && (
                  <>
                    <CustomButton
                      variant="outlined"
                      disabled={isLoading || periodCuotas === '' ? true : false}
                      onClick={() => {
                        if (!imponible) {
                          return;
                        }
                        const imprimirUrl = `${config.api.url}/pdf/boleta/${getTipoImponible(imponible)}-${getIdImponible(imponible)
                          }/${selectedResource?.recurso}?periodos=${periodCuotas}`;

                        window.open(imprimirUrl);
                      }}
                    >
                      Imprimir
                    </CustomButton>

                    <CustomButton
                      variant="outlined"
                      disabled={isLoading || selectedResource?.anual === null ? true : false}
                      onClick={() => {
                        const comprobanteURL = `${config.api.url}/pdf/comprobante/18-${selectedResource?.anual}`;
                        window.open(comprobanteURL);
                      }}
                    >
                      Comprobante Anual
                    </CustomButton>

                    <CustomButton
                      variant="outlined"
                      disabled={isLoading || selectedResource?.anual === null ? true : false}
                      onClick={() => {
                        const comprobanteURL = `${config.target.url}/paga-tu-tasa/qr/18-${selectedResource?.anual}`;
                        window.open(comprobanteURL);
                      }}
                    >
                      Pago Anual
                    </CustomButton>

                    <CustomButton
                      variant="contained"
                      disabled={isLoading || !selectedCuotas || !selectedCuotas.length || !cuotas.length}
                      onClick={handlePayment}
                      sx={{ color: 'white' }}
                    >
                      PAGAR AHORA
                    </CustomButton>
                  </>
                )}
              </Box> */}
            </ExpirationWrapper>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
